import { useMemo, useState } from 'react';
import { ResultCount } from '@jywer/ui';
import { CancellationBanner } from './CancellationBanner';
import { ClientGroup } from './ClientGroup';
import { useBillingData } from './useBillingData';
import { groupByClient } from './types';
import { SubscriptionEndTasks } from './SubscriptionEndTasks';

type StatusFilter = 'draft' | 'postponed' | 'approved';

const DEFAULT_STATUS: StatusFilter[] = ['draft', 'postponed'];

export function BillingPage() {
  const [statuses, setStatuses] = useState<StatusFilter[]>(DEFAULT_STATUS);
  const [search, setSearch] = useState('');

  const filters = useMemo(() => ({ status: statuses, invoiceType: [] as string[] }), [statuses]);

  const { invoices, loading, error, reload } = useBillingData(filters);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter(
      (i) =>
        i.client_name.toLowerCase().includes(q) ||
        (i.description?.toLowerCase().includes(q) ?? false),
    );
  }, [invoices, search]);

  const groups = useMemo(() => groupByClient(filtered), [filtered]);

  const toggleStatus = (s: StatusFilter) =>
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-primary">Billing</h1>
        <p className="text-sm text-ink-2">
          Ugentlig fakturagennemgang — godkend, udskyd, marker sendt.
        </p>
      </div>

      <CancellationBanner />

      <SubscriptionEndTasks />

      <div className="flex flex-wrap items-end gap-4 rounded border border-line-1 bg-paper-0 px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-2">Status</span>
          <div className="flex gap-1">
            {(['draft', 'postponed', 'approved'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  statuses.includes(s)
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line-2 bg-paper-0 text-ink-1 hover:border-line-2'
                }`}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søg kunde eller beskrivelse…"
          className="w-64 rounded border border-line-2 px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="ml-auto">
          <ResultCount
            shown={filtered.length}
            total={invoices.length}
            singularNoun="faktura"
            pluralNoun="fakturaer"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {error}
        </div>
      ) : null}

      {loading && invoices.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Henter fakturaer…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Ingen fakturaer at gennemgå — alt er up to date.
        </div>
      ) : (
        groups.map((g) => <ClientGroup key={g.clientId} group={g} onChanged={reload} />)
      )}
    </div>
  );
}

function statusLabel(s: StatusFilter): string {
  switch (s) {
    case 'draft':
      return 'Kladder';
    case 'postponed':
      return 'Udskudte';
    case 'approved':
      return 'Godkendte';
  }
}
